import { type Ref, ref } from 'vue'

import { useConfig } from '@/composables/config'
import { loadTurnstile } from '@/utils/load-turnstile'

type UseTurnstileOptions = {
    onSuccess?: (token: string) => void
    onError?: () => void
}

export const useTurnstile = (options: UseTurnstileOptions = {}) => {
    const { VUE_APP_TURNSTILE_SITE_KEY } = useConfig()

    const widgetId = ref<string>()
    const token = ref<string>()

    const render = async (container: Ref<HTMLElement | null>) => {
        if (!VUE_APP_TURNSTILE_SITE_KEY) {
            return
        }

        await loadTurnstile()

        if (!container?.value || !window.turnstile) {
            return
        }

        if (widgetId.value) {
            remove()
        }

        try {
            widgetId.value = window.turnstile.render(container.value, {
                sitekey: VUE_APP_TURNSTILE_SITE_KEY,
                callback: (receivedToken: string) => {
                    token.value = receivedToken
                    options.onSuccess?.(receivedToken)
                },
                'error-callback': () => {
                    token.value = undefined
                    options.onError?.()
                },
                theme: 'dark',
            })
        } catch (error) {
            console.error('Failed to render Turnstile widget:', error)
        }
    }

    const remove = () => {
        if (widgetId.value && window.turnstile) {
            try {
                window.turnstile.remove(widgetId.value)
                widgetId.value = undefined
                token.value = undefined
            } catch (error) {
                console.error('Failed to remove Turnstile widget:', error)
            }
        }
    }

    return {
        render,
        remove,
    }
}
